import React, { useMemo } from 'react'
import { Text, TextProps } from 'rebass'
import styled, {
  createGlobalStyle,
  css,
  DefaultTheme,
  ThemeProvider as StyledComponentsThemeProvider,
} from 'styled-components'
import { useIsDarkMode } from '../state/user/hooks'
import { Colors } from './styled'

export * from './components'

export const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280,
}

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
  (accumulator, size) => {
    ;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
      @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
        ${css(a, b, c)}
      }
    `
    return accumulator
  },
  {}
) as any

const white = '#FFFFFF'
const black = '#000000'

export function colors(darkMode: boolean): Colors {
  return {
    // base
    white,
    black,

    // text
    text1: darkMode ? '#FFFFFF' : '#FFFFFF',
    text2: '#C3C5CB',
    text3: '#6C7284',
    text4: '#565A69',
    text5: '#2C2F36',

    // backgrounds / greys
    bg0: '#0E0C15',
    bg1: '#18161F',
    bg2: '#1F1C29',
    bg3: '#40444F', // KO
    bg4: '#565A69', // KO
    bg5: '#6C7284', // KO
    bg6: '#1A2028', // KO

    // specialty colors
    modalBG: 'rgba(0,0,0,.425)',
    advancedBG: 'rgba(0,0,0,0.1)',

    // primary colors
    primary1: '#4AFB39',
    primary2: '#3680E7',
    primary3: '#4AFB39',
    primary4: '#4AFB3970',
    primary5: '#4AFB391A',

    // color text
    primaryText1: '#4AFB39',

    // secondary colors
    secondary1: '#4afc38',
    secondary2: '#17000b26',
    secondary3: '#17000b26',

    // other
    red1: '#FD4040',
    red2: '#F82D3A',
    red3: '#D60000',
    green1: '#27AE60',
    yellow1: '#e3a507',
    yellow2: '#ff8f00',
    yellow3: '#F3B71E',
    blue1: '#4AFB39',
    blue2: '#5199FF',

    error: '#FD4040',
    success: '#27AE60',
    warning: '#ff8f00',

    // dont wanna forget these blue yet
    // blue4: darkMode ? '#153d6f70' : '#C4D9F8',
    // blue5: darkMode ? '#153d6f70' : '#EBF4FF',
  }
}

export function theme(darkMode: boolean): DefaultTheme {
  return {
    ...colors(darkMode),

    grids: {
      sm: 8,
      md: 12,
      lg: 24,
    },

    //shadows
    shadow1: darkMode ? '#000' : '#2F80ED',

    // media queries
    mediaWidth: mediaWidthTemplates,

    // css snippets
    flexColumnNoWrap: css`
      display: flex;
      flex-flow: column nowrap;
    `,
    flexRowNoWrap: css`
      display: flex;
      flex-flow: row nowrap;
    `,
  }
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const darkMode = useIsDarkMode()

  const themeObject = useMemo(() => theme(darkMode), [darkMode])

  return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>
}

const TextWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
`

export const TYPE = {
  main(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text2'} {...props} />
  },
  link(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'primary1'} {...props} />
  },
  label(props: TextProps) {
    return <TextWrapper fontWeight={600} color={'text1'} {...props} />
  },
  black(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text1'} {...props} />
  },
  white(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'white'} {...props} />
  },
  body(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={16} color={'text1'} {...props} />
  },
  largeHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} {...props} />
  },
  mediumHeader(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={20} {...props} />
  },
  subHeader(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={14} {...props} />
  },
  small(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={11} {...props} />
  },
  blue(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'blue1'} {...props} />
  },
  yellow(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'yellow3'} {...props} />
  },
  darkGray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text3'} {...props} />
  },
  gray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'bg3'} {...props} />
  },
  italic(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={12} fontStyle={'italic'} color={'text2'} {...props} />
  },
  error({ error, ...props }: { error: boolean } & TextProps) {
    return <TextWrapper fontWeight={500} color={error ? 'red1' : 'text2'} {...props} />
  },
}

export const ThemedBackground = styled.div<{ backgroundColor?: string | undefined }>`
  position: fixed;
  top: 0;
  left: calc(-100vw / 2);
  right: 0;
  pointer-events: none;
  /* max-width: 100vw !important; */
  width: 200vw;
  height: 200vh;
  mix-blend-mode: color;
  transform: translateY(-100vh);
  will-change: background;
  transition: background 450ms ease;
`

export const FixedGlobalStyle = createGlobalStyle`
html, input, textarea, button {
  font-family: 'Inter', sans-serif;
  font-display: fallback;
}
@supports (font-variation-settings: normal) {
  html, input, textarea, button {
    font-family: 'Inter', sans-serif;
  }
}

html,
body {
  margin: 0;
  padding: 0;
}

 a {
   color: ${colors(false).blue1}; 
 }

* {
  box-sizing: border-box;
}

button {
  user-select: none;
}

html {
  font-size: 16px;
  font-variant: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  font-feature-settings: 'ss01' on, 'ss02' on,  'cv01' on, 'cv03' on;
  
}
`

export const ThemedGlobalStyle = createGlobalStyle`

body {
  --privy-color-background: ${({ theme }) => theme.bg0};
  --privy-color-background-2: ${({ theme }) => theme.bg1};

  #privy-dialog-backdrop {
    background-color: rgba(0, 0, 0, 0.2);
  }
}

html {  
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg0};

  @font-face {
    font-family: 'Unbounded';
    font-style: normal;
    font-weight: 900; /* Black */
    src: url('/fonts/unbounded/Unbounded-Black.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Unbounded';
    font-style: normal;
    font-weight: 700; /* Bold */
    src: url('/fonts/unbounded/Unbounded-Bold.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Unbounded';
    font-style: normal;
    font-weight: 800; /* ExtraBold */
    src: url('/fonts/unbounded/Unbounded-ExtraBold.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Unbounded';
    font-style: normal;
    font-weight: 200; /* ExtraLight */
    src: url('/fonts/unbounded/Unbounded-ExtraLight.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Unbounded';
    font-style: normal;
    font-weight: 300; /* Light */
    src: url('/fonts/unbounded/Unbounded-Light.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Unbounded';
    font-style: normal;
    font-weight: 500; /* Medium */
    src: url('/fonts/unbounded/Unbounded-Medium.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Unbounded';
    font-style: normal;
    font-weight: 400; /* Regular */
    src: url('/fonts/unbounded/Unbounded-Regular.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 900; /* Black */
    src: url('/fonts/inter/Inter_18pt-Black.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 900; /* Black Italic */
    src: url('/fonts/inter/Inter_18pt-BlackItalic.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 700; /* Bold */
    src: url('/fonts/inter/Inter_18pt-Bold.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 700; /* Bold Italic */
    src: url('/fonts/inter/Inter_18pt-BoldItalic.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 800; /* ExtraBold */
    src: url('/fonts/inter/Inter_18pt-ExtraBold.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 800; /* ExtraBold Italic */
    src: url('/fonts/inter/Inter_18pt-ExtraBoldItalic.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 200; /* ExtraLight */
    src: url('/fonts/inter/Inter_18pt-ExtraLight.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 200; /* ExtraLight Italic */
    src: url('/fonts/inter/Inter_18pt-ExtraLightItalic.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 400; /* Italic */
    src: url('/fonts/inter/Inter_18pt-Italic.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 300; /* Light */
    src: url('/fonts/inter/Inter_18pt-Light.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 300; /* Light Italic */
    src: url('/fonts/inter/Inter_18pt-LightItalic.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 500; /* Medium */
    src: url('/fonts/inter/Inter_18pt-Medium.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 500; /* Medium Italic */
    src: url('/fonts/inter/Inter_18pt-MediumItalic.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 400; /* Regular */
    src: url('/fonts/inter/Inter_18pt-Regular.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 600; /* SemiBold */
    src: url('/fonts/inter/Inter_18pt-SemiBold.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 600; /* SemiBold Italic */
    src: url('/fonts/inter/Inter_18pt-SemiBoldItalic.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 100; /* Thin */
    src: url('/fonts/inter/Inter_18pt-Thin.ttf') format('truetype');
  }
  
  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 100; /* Thin Italic */
    src: url('/fonts/inter/Inter_18pt-ThinItalic.ttf') format('truetype');
  }

  font-feature-settings: "calt" 0, "ss01" 0, "ss02" 0, "salt" 0;

  transition: all 200ms;

  background-color: #121212;

  ::-webkit-scrollbar {
    width: 6px; /* Width of the scrollbar */
    border-radius: 12px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #4afb39; /* Thumb color */
    border-radius: 12px; /* Rounded corners */
  }

  ::-webkit-scrollbar-track {
    background-color: #171717; /* Track background color */
  }

  /* Scrollbar styling for Firefox */
  scrollbar-width: thin; /* Thin scrollbar */
  scrollbar-color: #4afb39 #171717; /* Thumb color followed by track color */

  
  ::-webkit-scrollbar {
  width: 6px; /* Set the width of the scrollbar */
  }

  ::-webkit-scrollbar-thumb {
    background-color: #4AFB39; /* Set the color of the scrollbar */
    border-radius: 8px; /* Optional: Add rounded corners */
  }

  ::-webkit-scrollbar-track {
    background-color: #171717; /* Set the background color of the scrollbar track */
  }

  /* For Firefox */
  * {
    scrollbar-width: thin; /* Set the scrollbar width to thin */
    scrollbar-color: #4AFB39; /* Set the thumb and track colors */
  }
}
`
